import { useCallback, useEffect, useState } from "react";
import { uploadProblem } from "./documentsApi";

/**
 * A student's documents as a page holds them: the list, and the three moves
 * (upload, remove, verify), each reloading the list when it lands. `api` is
 * `staffDocumentsApi(studentId)` or `myDocumentsApi()`; `enabled` is the
 * module switch — off, nothing is fetched.
 *
 * A move that fails REJECTS with an Error whose message is the sentence to
 * show — under the row it belongs to, which is where `DocumentsPanel` puts it.
 * `error` here is only the list's own load failure.
 *
 * `load()` returns the whole profile read (work history, the evaluation, the
 * modules), so a page that needs more than the documents reads it from `profile`.
 */
export default function useStudentDocuments(api, enabled = true) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(!!enabled);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const reload = useCallback(async () => {
    if (!enabled || !api) {
      setProfile(null);
      setLoading(false);
      return null;
    }
    setLoading(true);
    try {
      const data = await api.list();
      setProfile(data);
      setError("");
      return data;
    } catch (err) {
      setError(err?.response?.data?.message || "Could not load the documents.");
      return null;
    } finally {
      setLoading(false);
    }
  }, [api, enabled]);

  useEffect(() => { reload(); }, [reload]);

  const act = useCallback(async (fn, fallback) => {
    setBusy(true);
    try {
      const out = await fn();
      await reload();
      return out;
    } catch (err) {
      throw new Error(uploadProblem(err, fallback));
    } finally {
      setBusy(false);
    }
  }, [reload]);

  const upload = useCallback((item, opts) => act(() => api.upload(item, opts), "The document could not be uploaded."), [act, api]);
  const remove = useCallback((doc) => act(() => api.remove(doc._id), "The document could not be removed."), [act, api]);
  const verify = useCallback(
    (doc, status, note) => act(() => api.verify(doc._id, { status, note }), "The document could not be updated."),
    [act, api],
  );

  return {
    profile,
    documents: profile?.documents || [],
    loading,
    busy,
    error,
    reload,
    upload,
    remove,
    verify: api?.verify ? verify : undefined,
  };
}
